import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'

let registered = false

/**
 * The original animates with Framer Motion springs configured as
 * `{ type: 'spring', bounce: 0, duration: 1 }`, i.e. a critically damped
 * spring: no overshoot, fast departure, long soft settle.
 *
 * Rather than approximating with `power3.out`, this reproduces the closed form
 * of a critically damped spring so the curve matches frame for frame:
 *   f(t) = 1 - (1 + wt)e^(-wt)
 *
 * w is normalised so the motion has visually settled at t = 1.
 */
const W = 9.2

export const springOut = (p: number): number => {
    const v = 1 - (1 + W * p) * Math.exp(-W * p)

    return v / (1 - (1 + W) * Math.exp(-W))
}

/** The stiffness/damping spring used for the slowest hero element. */
const STIFFNESS = 300
const DAMPING = 50
const MASS = 1

export const springStiff = (p: number): number => {
    const zeta = DAMPING / (2 * Math.sqrt(STIFFNESS * MASS))
    const wn = Math.sqrt(STIFFNESS / MASS)
    const t = p * 0.55

    if (zeta < 1) {
        const wd = wn * Math.sqrt(1 - zeta * zeta)

        return 1 - Math.exp(-zeta * wn * t) * (Math.cos(wd * t) + ((zeta * wn) / wd) * Math.sin(wd * t))
    }

    return 1 - (1 + wn * t) * Math.exp(-wn * t)
}

export const registerGsap = (): void => {
    if (registered || typeof window === 'undefined') return

    gsap.registerPlugin(ScrollTrigger)

    gsap.registerEase('springOut', springOut)
    gsap.registerEase('springStiff', springStiff)

    registered = true
}

export type RevealKind = 'up' | 'down' | 'left' | 'right' | 'scale' | 'pop' | 'fade' | 'clip'

/**
 * Initial states lifted verbatim from the original's server-rendered inline
 * styles. Scroll reveals travel 20px (10px for supporting copy); the page-load
 * hero sequence travels 50px; the gallery tiles pop from `scale(0.2)` with a
 * per-tile rotation.
 */
export const initialFor = (
    kind: RevealKind,
    distance: number,
    rotate: number
): gsap.TweenVars => {
    switch (kind) {
        case 'up':
            return { opacity: 0, y: distance }
        case 'down':
            return { opacity: 0, y: -distance }
        case 'left':
            return { opacity: 0, x: -distance }
        case 'right':
            return { opacity: 0, x: distance }
        case 'scale':
            return { opacity: 0, scale: 0.92 }
        case 'pop':
            return { opacity: 0, scale: 0.2, rotate }
        case 'clip':
            return { opacity: 1, clipPath: 'inset(100% 0% 0% 0%)' }
        case 'fade':
        default:
            return { opacity: 0 }
    }
}

export const settledFor = (kind: RevealKind): gsap.TweenVars => {
    if (kind === 'clip') return { opacity: 1, clipPath: 'inset(0% 0% 0% 0%)' }

    if (kind === 'fade') return { opacity: 1 }

    if (kind === 'pop') return { opacity: 1, scale: 1, rotate: 0 }

    return { opacity: 1, x: 0, y: 0, scale: 1 }
}

export { gsap, ScrollTrigger }
